// Shared fiscal year utilities — used by DateFilterContext and all pages

export interface FiscalYearOption {
  label: string;      // e.g. "2025-26 (Apr 2025 – Mar 2026)"
  startDate: string;  // e.g. "2025-04-01"
  endDate: string;    // e.g. "2026-03-31"
  value: string;      // e.g. "2025-2026"
}

const MONTH_NAMES_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Format month number (1-12) to short name, e.g. 4 → "Apr" */
export function monthName(month: number): string {
  return MONTH_NAMES_EN[month - 1] || String(month);
}

/** Build 6 fiscal year options (current + 5 prior) based on company fiscal calendar */
export function buildFiscalYearOptions(
  fiscalStartMD: string,  // e.g. "04-01" or "2026-04-01"
  fiscalEndMD: string,    // e.g. "03-31" or "2027-03-31"
): FiscalYearOption[] {
  // Normalize: handle "MM", "MM-DD", "YYYY-MM" and "YYYY-MM-DD" formats. A
  // missing day (month-only, e.g. "04" or the fiscal-period UI's "2025-04")
  // yields `null`, which callers resolve to the 1st (start) or the month's
  // last day (end) — never to the literal string "undefined".
  const parseMonthDay = (raw: string): { month: number; day: number | null } => {
    const parts = String(raw ?? '').split('-').map(Number);
    let month: number;
    let day: number | null;
    if (parts.length >= 3) { month = parts[1]; day = parts[2]; }
    else if (parts.length === 2) {
      if (parts[0] > 12) { month = parts[1]; day = null; } // YYYY-MM
      else { month = parts[0]; day = parts[1]; }           // MM-DD
    }
    else { month = parts[0]; day = null; }
    if (!Number.isFinite(month) || month < 1 || month > 12) month = NaN;
    if (day !== null && (!Number.isFinite(day) || day < 1 || day > 31)) day = null;
    return { month, day };
  };

  const parsedStart = parseMonthDay(fiscalStartMD);
  const parsedEnd = parseMonthDay(fiscalEndMD);
  const sm = Number.isFinite(parsedStart.month) ? parsedStart.month : 4; // start month
  const sd = parsedStart.day;                                          // start day (or null)
  const em = Number.isFinite(parsedEnd.month) ? parsedEnd.month : 3;   // end month
  const ed = parsedEnd.day;                                            // end day (or null)

  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  let baseYear = now.getFullYear();
  if (currentMonth < sm) baseYear--;

  const startMonthName = monthName(sm);
  const endMonthName = monthName(em);

  const opts: FiscalYearOption[] = [];
  for (let i = 5; i >= 0; i--) {
    const sy = baseYear - i;
    // Fiscal year crosses calendar year if end month <= start month
    const ey = em <= sm ? sy + 1 : sy;
    const startDay = sd ?? 1;
    const endDay = ed ?? new Date(ey, em, 0).getDate(); // last day of the end month
    const sD = `${sy}-${String(sm).padStart(2, '0')}-${String(startDay).padStart(2, '0')}`;
    const eD = `${ey}-${String(em).padStart(2, '0')}-${String(endDay).padStart(2, '0')}`;
    const shortSy = String(sy).slice(2);
    const shortEy = String(ey).slice(2);

    if (em <= sm) {
      // Cross-year fiscal year: e.g. Apr 2025 – Mar 2026
      opts.push({
        label: `${shortSy}-${shortEy} (${startMonthName} ${sy} – ${endMonthName} ${ey})`,
        startDate: sD,
        endDate: eD,
        value: `${sy}-${ey}`,
      });
    } else {
      // Same-year fiscal year: e.g. Jan 2025 – Dec 2025
      opts.push({
        label: `${sy} (${startMonthName} – ${endMonthName} ${sy})`,
        startDate: sD,
        endDate: eD,
        value: `${sy}-${sy}`,
      });
    }
  }
  return opts;
}
